import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { LayoutGrid, Package, ShoppingCart, TrendingUp, Wallet, FileText, Store } from "lucide-react";
import VendorAgreement from "@/pages/vendor/VendorAgreement";

const REG_STAGE = {
  submitted: "Submitted — awaiting review",
  under_verification: "Under verification",
  documents_pending: "Documents pending",
  commercial_review: "Commercial review",
};

export default function VendorLayout() {
  const [vendor, setVendor] = useState(null);
  const [reg, setReg] = useState(undefined); // undefined = not checked, null = none
  const [agreementOk, setAgreementOk] = useState(true); // assume ok until checked
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vendors/me");
      setVendor(data);
      if (data?.status === "approved") {
        try { setAgreementOk((await api.get("/vendor/agreement")).data.accepted); }
        catch { setAgreementOk(true); } // don't lock out on a fetch error
      }
    } catch {
      setVendor(null);
      try { const { data } = await api.get("/vendor/registration"); setReg(data || null); }
      catch { setReg(null); }
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="p-24 font-mono text-sm">Loading...</div>;

  // No storefront yet → gate on the detailed registration + admin approval.
  if (!vendor) {
    const status = reg?.status;
    const inReview = status && status !== "rejected";
    return (
      <div className="max-w-[720px] mx-auto px-6 md:px-12 py-20">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Vendor onboarding</p>
        {inReview ? (
          <>
            <h1 className="font-serif text-5xl tracking-tighter mt-2">Application under review.</h1>
            <div className="mt-8 border border-foreground p-6 md:p-8">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Status</p>
              <p className="font-serif text-2xl mt-1 text-[hsl(var(--accent))]">{REG_STAGE[status] || status.replace(/_/g, " ")}</p>
              <p className="mt-4 text-muted-foreground text-sm leading-relaxed">
                The Spotters team is reviewing your details ({reg.trade_name || reg.legal_name}). You'll get access to your console the moment it's approved. This usually takes 1–2 business days.
              </p>
            </div>
          </>
        ) : (
          <>
            <h1 className="font-serif text-5xl tracking-tighter mt-2">{status === "rejected" ? "Application needs changes." : "Complete your registration."}</h1>
            <p className="mt-6 text-muted-foreground max-w-md leading-relaxed">
              {status === "rejected"
                ? "Your previous submission was returned. Please review and re-submit with the corrected details."
                : "Before your store goes live, we need your business details — PAN/GST, banking and documents. It takes about 10 minutes."}
            </p>
            {reg?.admin_notes && <p className="mt-3 text-sm text-destructive">Note: {reg.admin_notes}</p>}
            <Link to="/vendor-register" className="inline-block mt-8 bg-foreground text-background px-6 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[hsl(var(--accent))]">
              {status === "rejected" ? "Re-submit registration" : "Start registration"} →
            </Link>
          </>
        )}
      </div>
    );
  }

  // Hard gate: an approved vendor must accept the current agreement before the
  // console (and product listing) unlocks.
  if (vendor.status === "approved" && !agreementOk) {
    return <VendorAgreement gate onAccepted={load} />;
  }

  const links = [
    { to: "/vendor", label: "Overview", icon: LayoutGrid, end: true },
    { to: "/vendor/products", label: "Products", icon: Package },
    { to: "/vendor/orders", label: "Orders", icon: ShoppingCart },
    { to: "/vendor/analytics", label: "Analytics", icon: TrendingUp },
    { to: "/vendor/payouts", label: "Payouts", icon: Wallet },
    { to: "/vendor/profile", label: "Profile", icon: Store },
    { to: "/vendor/agreement", label: "Agreement", icon: FileText },
  ];

  return (
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-12 grid md:grid-cols-[220px_1fr] gap-10">
      <aside className="md:sticky md:top-8 self-start">
        <div className="border border-foreground p-5 tag-punch pl-10 mb-6">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Vendor Console</p>
          <p className="font-serif text-xl mt-1 leading-tight" data-testid="vendor-name">{vendor.store_name}</p>
          <p className="font-mono text-[10px] mt-2">
            Status: <span className={vendor.status === "approved" ? "text-[hsl(var(--accent))]" : "text-destructive"}>{vendor.status.toUpperCase()}</span>
          </p>
        </div>
        <nav className="flex flex-col">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`vnav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 border-l-2 font-mono text-xs uppercase tracking-widest transition-colors ${
                  isActive
                    ? "border-[hsl(var(--accent))] bg-secondary text-[hsl(var(--accent))]"
                    : "border-transparent hover:border-foreground hover:bg-secondary"
                }`
              }
            >
              <l.icon size={14} />
              {l.label}
            </NavLink>
          ))}
        </nav>
      </aside>
      <div>
        <Outlet context={{ vendor, reloadVendor: load }} />
      </div>
    </div>
  );
}
